import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import { format } from 'date-fns';

const TYPE_META = {
  exact: {
    label: 'Duplicado exacto',
    badge: 'bg-red-900/40 text-red-300 border border-red-700',
    hint: 'Contenido binario idéntico (mismo archivo).',
  },
  probable: {
    label: 'Duplicado probable',
    badge: 'bg-amber-900/40 text-amber-300 border border-amber-700',
    hint: 'Los metadatos indican que probablemente es la misma canción.',
  },
  possible: {
    label: 'Posible coincidencia',
    badge: 'bg-violet-900/40 text-violet-300 border border-violet-700',
    hint: 'Hay similitud pero requiere revisión manual.',
  },
};

const SORTS = [
  { value: 'artist', label: 'Artista' },
  { value: 'title', label: 'Título' },
  { value: 'count', label: 'Cantidad de duplicados' },
  { value: 'size', label: 'Tamaño total' },
  { value: 'score', label: 'Coincidencia' },
  { value: 'folder', label: 'Carpeta' },
];

const FORMATS = ['', 'mp3', 'flac', 'wav', 'ogg', 'm4a', 'aac'];

function fmtDuration(s) {
  if (s === null || s === undefined || s === 0) return '—';
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
}

function fmtBytes(b) {
  if (!b) return '—';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = b;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function fmtDate(d) {
  if (!d) return '—';
  try { return format(new Date(d), 'dd/MM/yy HH:mm'); } catch { return '—'; }
}

export default function DuplicateScanner() {
  const [folders, setFolders] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState(new Set());
  const [allLibrary, setAllLibrary] = useState(true);
  const [scans, setScans] = useState([]);
  const [scan, setScan] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    types: { exact: true, probable: true, possible: true },
    folder_id: '',
    artist: '',
    format: '',
    sort: 'artist',
    direction: 'asc',
  });
  const [results, setResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [compare, setCompare] = useState([]);
  const pollRef = useRef(null);

  const isActive = scan && (scan.status === 'queued' || scan.status === 'running');

  const loadFolders = useCallback(async () => {
    try {
      const { data } = await api.get('/api/music/folders');
      setFolders((data || []).filter((f) => f.category === 'music'));
    } catch (err) {
      console.error('[Duplicates] Error loading folders:', err);
    }
  }, []);

  const loadScans = useCallback(async () => {
    try {
      const { data } = await api.get('/api/duplicates/scans', { params: { limit: 15 } });
      setScans(data || []);
      return data || [];
    } catch (err) {
      console.error('[Duplicates] Error loading scans:', err);
      return [];
    }
  }, []);

  const fetchResults = useCallback(async (scanId, f) => {
    setLoadingResults(true);
    try {
      const types = Object.entries(f.types).filter(([, v]) => v).map(([k]) => k).join(',');
      const params = {
        types: types || 'none',
        folder_id: f.folder_id || undefined,
        artist: f.artist || undefined,
        format: f.format || undefined,
        sort: f.sort,
        direction: f.direction,
      };
      const { data } = await api.get(`/api/duplicates/scans/${scanId}/results`, { params });
      setResults(data);
    } catch (err) {
      const msg = err.response?.data?.error === 'Scan not completed' ? null : 'Error al cargar resultados';
      if (msg) setError(msg);
      setResults(null);
    } finally {
      setLoadingResults(false);
    }
  }, []);

  const pollScan = useCallback(async (scanId) => {
    try {
      const { data } = await api.get(`/api/duplicates/scans/${scanId}`);
      setScan(data);
      if (data.status === 'completed') {
        fetchResults(scanId, filters);
      } else if (data.status === 'failed') {
        setError(`El escaneo falló: ${data.error || 'error desconocido'}`);
      }
    } catch (err) {
      console.error('[Duplicates] Error polling scan:', err);
    }
  }, [fetchResults, filters]);

  useEffect(() => {
    loadFolders();
    loadScans().then((list) => {
      if (list.length > 0) {
        setScan(list[0]);
        if (list[0].status === 'completed') {
          fetchResults(list[0].id, filters);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isActive) {
      if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setTimeout(() => pollScan(scan.id), 2000);
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [isActive, scan, pollScan]);

  useEffect(() => {
    if (scan && scan.status === 'completed' && results) {
      fetchResults(scan.id, filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const startScan = async () => {
    setStarting(true);
    setError(null);
    setResults(null);
    setCompare([]);
    try {
      const body = allLibrary || selectedFolders.size === 0 ? {} : { folder_ids: [...selectedFolders] };
      const { data } = await api.post('/api/duplicates/scans', body);
      setScan(data);
      loadScans();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo iniciar el escaneo');
    } finally {
      setStarting(false);
    }
  };

  const selectScan = (s) => {
    setScan(s);
    setCompare([]);
    setError(null);
    if (s.status === 'completed') fetchResults(s.id, filters);
    else setResults(null);
  };

  const toggleFolder = (id) => {
    setAllLibrary(false);
    setSelectedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleCompare = (file) => {
    setCompare((prev) => {
      if (prev.some((f) => f.id === file.id)) return prev.filter((f) => f.id !== file.id);
      if (prev.length >= 2) return [prev[1], file];
      return [...prev, file];
    });
  };

  const summary = scan?.summary || {};
  const progressPct = scan && scan.total_files > 0
    ? Math.min(100, Math.round((scan.processed_files / scan.total_files) * 100))
    : (scan?.status === 'running' ? 1 : 0);
  const activeTypes = Object.entries(filters.types).filter(([, v]) => v).map(([k]) => k).length;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Encabezado */}
      <div>
        <h1 className="text-2xl font-bold text-dark-100">Canciones duplicadas</h1>
        <p className="mt-1 text-sm text-dark-400">
          Detecta canciones repetidas dentro de una carpeta o en toda la biblioteca.{' '}
          <span className="rounded bg-dark-800 px-1.5 py-0.5 text-[11px] font-semibold text-success-400">READ-ONLY</span>{' '}
          — esta herramienta no elimina, mueve ni modifica archivos: la decisión siempre es tuya.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-red-800 bg-red-900/30 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Configuración del escaneo */}
      <div className="rounded-lg border border-dark-700 bg-dark-850 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-dark-100">Área a analizar</h2>
            <p className="text-xs text-dark-400">Elegí carpetas específicas o escaneá toda la biblioteca musical.</p>
          </div>
          <button
            onClick={startScan}
            disabled={starting || isActive}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isActive ? 'Escaneando…' : starting ? 'Iniciando…' : 'Iniciar escaneo'}
          </button>
        </div>

        <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm text-dark-200">
          <input
            type="checkbox"
            checked={allLibrary}
            onChange={(e) => setAllLibrary(e.target.checked)}
            className="h-4 w-4 rounded border-dark-600 bg-dark-800"
          />
          Toda la biblioteca musical
        </label>

        {!allLibrary && (
          <div className="mt-3 flex flex-wrap gap-2">
            {folders.length === 0 && <span className="text-xs text-dark-500">No hay carpetas de música.</span>}
            {folders.map((f) => (
              <button
                key={f.id}
                onClick={() => toggleFolder(f.id)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                  selectedFolders.has(f.id)
                    ? 'bg-primary-600 text-white'
                    : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
                }`}
              >
                {f.name} ({f.file_count})
              </button>
            ))}
          </div>
        )}

        {/* Estado del escaneo activo */}
        {scan && (
          <div className="mt-4 rounded-md border border-dark-700 bg-dark-900/60 p-3">
            <div className="flex items-center justify-between text-xs text-dark-300">
              <span>
                Escaneo <span className="font-mono">{scan.id.slice(0, 8)}</span>
                {scan.folder_ids === null ? ' · toda la biblioteca' : ` · ${scan.folder_ids.length} carpeta(s)`}
              </span>
              <span className="font-medium">
                {scan.status === 'completed' && 'Completado'}
                {scan.status === 'running' && `Analizando… ${scan.processed_files}/${scan.total_files} archivos (${progressPct}%)`}
                {scan.status === 'queued' && 'En cola…'}
                {scan.status === 'failed' && 'Falló'}
              </span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-dark-800">
              <div
                className={`h-full rounded-full transition-all ${scan.status === 'completed' ? 'bg-success-500' : 'bg-primary-500'}`}
                style={{ width: `${scan.status === 'completed' ? 100 : progressPct}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Resumen */}
      {scan?.status === 'completed' && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <div className="rounded-lg border border-red-800/50 bg-red-900/20 p-3">
            <p className="text-xs text-red-300">Duplicados exactos</p>
            <p className="text-xl font-bold text-dark-100">{summary.groups_exact ?? 0}</p>
          </div>
          <div className="rounded-lg border border-amber-800/50 bg-amber-900/20 p-3">
            <p className="text-xs text-amber-300">Probables</p>
            <p className="text-xl font-bold text-dark-100">{summary.groups_probable ?? 0}</p>
          </div>
          <div className="rounded-lg border border-violet-800/50 bg-violet-900/20 p-3">
            <p className="text-xs text-violet-300">Posibles</p>
            <p className="text-xl font-bold text-dark-100">{summary.groups_possible ?? 0}</p>
          </div>
          <div className="rounded-lg border border-dark-700 bg-dark-850 p-3">
            <p className="text-xs text-dark-400">Archivos analizados</p>
            <p className="text-xl font-bold text-dark-100">{summary.files_analyzed ?? 0}</p>
          </div>
          <div className="rounded-lg border border-dark-700 bg-dark-850 p-3">
            <p className="text-xs text-dark-400">Espacio en exactos</p>
            <p className="text-xl font-bold text-dark-100">{fmtBytes(summary.wasted_bytes_exact)}</p>
          </div>
        </div>
      )}

      {/* Filtros */}
      {results && (
        <div className="rounded-lg border border-dark-700 bg-dark-850 p-4">
          <div className="flex flex-wrap items-center gap-3">
            {['exact', 'probable', 'possible'].map((t) => (
              <label key={t} className="flex cursor-pointer items-center gap-1.5 text-xs text-dark-200" title={TYPE_META[t].hint}>
                <input
                  type="checkbox"
                  checked={filters.types[t]}
                  onChange={(e) => setFilters((f) => ({ ...f, types: { ...f.types, [t]: e.target.checked } }))}
                  className="h-3.5 w-3.5 rounded border-dark-600 bg-dark-800"
                />
                <span className={TYPE_META[t].badge + ' rounded px-1.5 py-0.5 text-[10px] font-semibold'}>
                  {TYPE_META[t].label}
                </span>
              </label>
            ))}
            <span className="h-4 w-px bg-dark-700" />
            <select
              value={filters.folder_id}
              onChange={(e) => setFilters((f) => ({ ...f, folder_id: e.target.value }))}
              className="rounded-md border border-dark-600 bg-dark-800 px-2 py-1 text-xs text-dark-200"
            >
              <option value="">Todas las carpetas</option>
              {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <select
              value={filters.format}
              onChange={(e) => setFilters((f) => ({ ...f, format: e.target.value }))}
              className="rounded-md border border-dark-600 bg-dark-800 px-2 py-1 text-xs text-dark-200"
            >
              {FORMATS.map((x) => <option key={x} value={x}>{x === '' ? 'Todos los formatos' : x.toUpperCase()}</option>)}
            </select>
            <input
              value={filters.artist}
              onChange={(e) => setFilters((f) => ({ ...f, artist: e.target.value }))}
              placeholder="Filtrar por artista…"
              className="w-44 rounded-md border border-dark-600 bg-dark-800 px-2 py-1 text-xs text-dark-200 placeholder:text-dark-500"
            />
            <span className="h-4 w-px bg-dark-700" />
            <select
              value={filters.sort}
              onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))}
              className="rounded-md border border-dark-600 bg-dark-800 px-2 py-1 text-xs text-dark-200"
            >
              {SORTS.map((s) => <option key={s.value} value={s.value}>Ordenar: {s.label}</option>)}
            </select>
            <button
              onClick={() => setFilters((f) => ({ ...f, direction: f.direction === 'asc' ? 'desc' : 'asc' }))}
              className="rounded-md border border-dark-600 bg-dark-800 px-2 py-1 text-xs text-dark-300 hover:bg-dark-700"
              title={filters.direction === 'asc' ? 'Ascendente' : 'Descendente'}
            >
              {filters.direction === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      )}

      {/* Grupos de duplicados */}
      {loadingResults && <p className="text-sm text-dark-400">Cargando resultados…</p>}
      {results && !loadingResults && (
        <div className="space-y-4">
          {results.total_groups === 0 && activeTypes > 0 && (
            <div className="rounded-lg border border-success-800/50 bg-success-900/10 p-6 text-center text-sm text-success-300">
              No se encontraron duplicados con los filtros actuales.
            </div>
          )}
          {results.groups.map((g, gi) => (
            <div key={`${g.type}-${gi}`} className="overflow-hidden rounded-lg border border-dark-700 bg-dark-850">
              <div className="flex flex-wrap items-center gap-2 border-b border-dark-700 bg-dark-800/50 px-4 py-2.5">
                <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${TYPE_META[g.type].badge}`}>
                  {TYPE_META[g.type].label}
                </span>
                <span className="text-sm font-semibold text-dark-100">
                  {g.artist || 'Desconocido'} — {g.title || g.files[0]?.original_filename}
                </span>
                {g.type !== 'exact' && (
                  <span className="rounded bg-dark-800 px-1.5 py-0.5 text-[11px] text-dark-300">
                    Coincidencia: {g.score}%
                  </span>
                )}
                <span className="text-[11px] text-dark-400">{g.files.length} archivos</span>
                {g.hash && (
                  <span className="font-mono text-[10px] text-dark-500" title="SHA-256 del contenido">
                    {g.hash.slice(0, 16)}…
                  </span>
                )}
              </div>

              {g.reasons?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 border-b border-dark-700/50 px-4 py-2">
                  {g.reasons.map((r, i) => (
                    <span key={i} className="rounded bg-dark-800 px-1.5 py-0.5 text-[10px] text-dark-400">{r}</span>
                  ))}
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="text-dark-500">
                    <tr className="border-b border-dark-700/50">
                      <th className="px-3 py-2 font-medium">Carpeta</th>
                      <th className="px-3 py-2 font-medium">Archivo</th>
                      <th className="px-3 py-2 font-medium">Artista / Título</th>
                      <th className="px-3 py-2 font-medium">Duración</th>
                      <th className="px-3 py-2 font-medium">Formato</th>
                      <th className="px-3 py-2 font-medium">Bitrate</th>
                      <th className="px-3 py-2 font-medium">Tamaño</th>
                      <th className="px-3 py-2 font-medium">Subidas</th>
                      <th className="px-3 py-2 font-medium">Comparar</th>
                    </tr>
                  </thead>
                  <tbody className="text-dark-300">
                    {g.files.map((f) => (
                      <tr key={f.id} className="border-b border-dark-700/30 hover:bg-dark-800/40">
                        <td className="px-3 py-2">
                          {f.folder_name || <span className="text-dark-500">—</span>}
                          {f.is_corrupt && <span className="ml-1 rounded bg-red-900/50 px-1 text-[9px] text-red-300">corrupto</span>}
                          {!f.is_enabled && <span className="ml-1 rounded bg-dark-700 px-1 text-[9px] text-dark-400">deshabilitado</span>}
                        </td>
                        <td className="max-w-[220px] truncate px-3 py-2">
                          <Link to={`/media/${f.id}`} className="text-primary-400 hover:underline" title={f.original_filename}>
                            {f.original_filename}
                          </Link>
                          {f.exact_hash && g.type !== 'exact' && (
                            <span className="ml-1 rounded bg-red-900/40 px-1 text-[9px] text-red-300" title="Este archivo es copia exacta de otro del grupo">=hash</span>
                          )}
                        </td>
                        <td className="max-w-[220px] truncate px-3 py-2" title={`${f.artist || ''} — ${f.title || ''}`}>
                          {f.artist || <span className="text-dark-500">?</span>} — {f.title || <span className="text-dark-500">?</span>}
                          {f.album && <span className="text-dark-500"> · {f.album}</span>}
                        </td>
                        <td className="px-3 py-2">{fmtDuration(f.duration_seconds)}</td>
                        <td className="px-3 py-2 uppercase">{f.format || '—'}</td>
                        <td className="px-3 py-2">{f.bitrate ? `${f.bitrate}k` : '—'}</td>
                        <td className="px-3 py-2">{fmtBytes(f.size_bytes)}</td>
                        <td className="px-3 py-2" title={f.file_mtime_ms ? `Modificado: ${fmtDate(new Date(f.file_mtime_ms))}` : ''}>{f.play_count}</td>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={compare.some((c) => c.id === f.id)}
                            onChange={() => toggleCompare(f)}
                            className="h-3.5 w-3.5 rounded border-dark-600 bg-dark-800"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Historial de escaneos */}
      {scans.length > 0 && (
        <div className="rounded-lg border border-dark-700 bg-dark-850 p-4">
          <h2 className="mb-2 text-sm font-semibold text-dark-100">Escaneos anteriores</h2>
          <div className="space-y-1">
            {scans.map((s) => (
              <div
                key={s.id}
                className={`flex cursor-pointer items-center justify-between rounded px-3 py-2 text-xs hover:bg-dark-800 ${scan?.id === s.id ? 'bg-dark-800' : ''}`}
                onClick={() => selectScan(s)}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                      s.status === 'completed' ? 'bg-success-900/40 text-success-300'
                      : s.status === 'failed' ? 'bg-red-900/40 text-red-300'
                      : 'bg-primary-900/40 text-primary-300'
                    }`}
                  >
                    {s.status}
                  </span>
                  <span className="text-dark-300">
                    {s.folder_ids === null ? 'Toda la biblioteca' : `${s.folder_ids.length} carpeta(s)`}
                  </span>
                  {s.status === 'completed' && s.summary && (
                    <span className="text-dark-500">
                      {s.summary.groups_exact || 0} exactos · {s.summary.groups_probable || 0} probables · {s.summary.groups_possible || 0} posibles
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-dark-500">{fmtDate(s.created_at)}</span>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await api.delete(`/api/duplicates/scans/${s.id}`);
                        const list = await loadScans();
                        if (scan?.id === s.id) { setScan(list[0] || null); setResults(null); }
                      } catch { /* ignore */ }
                    }}
                    className="text-dark-500 hover:text-red-400"
                    title="Borrar este reporte (no toca archivos)"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comparador lado a lado */}
      {compare.length === 2 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setCompare([])}>
          <div
            className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-lg border border-dark-600 bg-dark-850 p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-dark-100">Comparación de archivos</h3>
              <button onClick={() => setCompare([])} className="text-dark-400 hover:text-dark-200">✕</button>
            </div>
            <table className="w-full text-left text-xs">
              <thead className="text-dark-500">
                <tr className="border-b border-dark-700">
                  <th className="px-2 py-2 font-medium">Información</th>
                  <th className="px-2 py-2 font-medium text-dark-300">Archivo A</th>
                  <th className="px-2 py-2 font-medium text-dark-300">Archivo B</th>
                </tr>
              </thead>
              <tbody className="text-dark-300">
                {[
                  ['Artista', (f) => f.artist || '—'],
                  ['Título', (f) => f.title || '—'],
                  ['Álbum', (f) => f.album || '—'],
                  ['Duración', (f) => fmtDuration(f.duration_seconds)],
                  ['Formato', (f) => (f.format || '—').toUpperCase()],
                  ['Bitrate', (f) => (f.bitrate ? `${f.bitrate} kbps` : '—')],
                  ['Tamaño', (f) => fmtBytes(f.size_bytes)],
                  ['Carpeta', (f) => f.folder_name || '—'],
                  ['Archivo', (f) => f.original_filename],
                  ['Reproducciones', (f) => f.play_count],
                  ['Hash (SHA-256)', (f) => f.sha256 ? `${f.sha256.slice(0, 24)}…` : '—'],
                ].map(([label, get]) => (
                  <tr key={label} className="border-b border-dark-700/30">
                    <td className="px-2 py-2 text-dark-500">{label}</td>
                    <td className="px-2 py-2">{get(compare[0])}</td>
                    <td className="px-2 py-2">{get(compare[1])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-[11px] text-dark-500">
              La herramienta no elige por vos: usá esta comparación para decidir manualmente cuál conservar.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
